import React, { useContext, useEffect, useState } from "react"
import classNames from "../../../../../utils/classNames"
import {
  ArchiveBoxXMarkIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline"
import { DateTime } from "luxon"
import { AdminContext } from "~/contexts/AdminContext"
import { ArrowTopRightOnSquareIcon, ReceiptRefundIcon } from "@heroicons/react/20/solid"
import HoverTip from "../../../../ui/HoverTip"
import Button from "../../../../ui/Button"
import RefundModalContents from "../../../../Reservations/RefundModal/RefundModalContents"
import IPayment from "../../../../../interfaces/IPayment"
import { useOrder } from "../../ReservationHooks/useOrder"
import { useHistory } from "../../ReservationHooks/useHistory"
import IHistoryRecord from "~/interfaces/IHistoryRecord"
import { CampProps, ReservationProps } from "~/interfaces"
import IOrder from "~/interfaces/IOrder"
import { useReservation } from "../../ReservationHooks/useReservation"
import eAdminUserRole from "~/enums/eAdminUserRole"
import ePaymentMode from "~/enums/ePaymentMode"
import useRevertPayment from "~/hooks/useRevertPayment"
import { usePreviews } from "~/components/Admin/Reservations/ReservationHooks/usePreviews"
import { serviceRateMode } from "~/api2/orders"
import { STATUSES } from "~/constants"
import { useMe } from "~/api2/hooks/useMe"
import { EyeIcon } from "@heroicons/react/16/solid"
import { formatPhoneNumber } from "~/utils"
import { isMobile } from "react-device-detect"

interface ComponentProps {
  reservation?: ReservationProps
  orderId: string | undefined
  setSlideoverOpen: (value: boolean) => void
}

const ReservationHistory = (props: ComponentProps) => {
  if (!props.reservation) return null
  const { camp, showModal, currentUserRole } = useContext(AdminContext)
  const { data: order, refetch: refetchOrder } = useOrder({ orderId: props.orderId })
  const { refetch: refetchPreview } = usePreviews({
    serviceRate: serviceRateMode.payment_link,
    isPaid: props.reservation?.status === STATUSES.PAID,
    orderId: props.reservation?.order_id,
    campingStyleId: props.reservation?.selected_camping_style_id || props.reservation?.site?.camping_style_id,
    discount_type: props.reservation?.discount_type,
  })
  const {
    data: historyData,
    refetch: refetchHistory,
    isLoading: isLoadingHistory,
  } = useHistory({ reservationId: props.reservation.id })
  const {
    index: { data: reservationData },
  } = useReservation({ reservationId: props.reservation.id })
  const { revertPayment, isLoading: isLoadingRecord } = useRevertPayment({
    reservationId: props.reservation.id,
    orderId: props.reservation?.order_id,
    refetchPreview: refetchPreview,
  })
  const history = historyData?.history

  const { revertedPaymentIds } = (history &&
    [...history].reverse().reduce(
      (acc, record) => {
        if (record.kind === "paid" && record.resource) {
          const payment = record.resource as IPayment
          if (payment.amount_in_cents > 0) {
            if (acc.negatives.has(-payment.amount_in_cents)) {
              acc.revertedPaymentIds.add(payment.id)
              const count = acc.negatives.get(-payment.amount_in_cents)!
              count === 1
                ? acc.negatives.delete(-payment.amount_in_cents)
                : acc.negatives.set(-payment.amount_in_cents, count - 1)
            } else {
              acc.positives.push({ id: payment.id, amount: payment.amount_in_cents })
            }
          } else if (payment.amount_in_cents < 0) {
            const matchIndex = acc.positives.findIndex((p) => p.amount === -payment.amount_in_cents)
            if (matchIndex !== -1) {
              acc.revertedPaymentIds.add(acc.positives[matchIndex].id)
              acc.positives.splice(matchIndex, 1)
            } else {
              const count = acc.negatives.get(payment.amount_in_cents) || 0
              acc.negatives.set(payment.amount_in_cents, count + 1)
            }
          }
        }
        return acc
      },
      {
        positives: [] as { id: string; amount: number }[],
        negatives: new Map<number, number>(),
        revertedPaymentIds: new Set<string>(),
      },
    )) || { positives: [], negatives: new Map<number, number>(), revertedPaymentIds: new Set<string>() }

  const handleRevert = (record: IHistoryRecord) => {
    const payment = record.kind === "paid" ? (record.resource as IPayment) : undefined
    if (payment && payment.id) {
      revertPayment(record.resource.id)
    }
  }

  useEffect(() => {
    refetchHistory()
  }, [reservationData?.reservation?.lock_version])

  const handleClickRefund = (payment: IPayment, externalUrl: string | undefined) => {
    props.setSlideoverOpen(false)
    showModal(
      <RefundModalContents payment={payment} initialAmount={payment.amount_in_cents / 100} externalUrl={externalUrl} order={order.order} />,
      true,
      "max-w-2xl",
      {
        closeCallback: () => {
          refetchHistory()
          refetchOrder()
          refetchPreview()
          props.setSlideoverOpen(true)
        },
      },
    )
  }

  return (
    <div className="mt-12 mb-12">
      <div className="text-xl font-bold mb-2">History</div>
      {isLoadingHistory ? (
        <ul role="list" className="mt-3 space-y-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <li key={index} className="relative flex gap-x-4">
              <div
                className={classNames(
                  index === 2 ? "h-6" : "-bottom-6",
                  "absolute left-0 top-0 flex w-6 justify-center",
                )}
              >
                <div className="w-px bg-gray-200" />
              </div>
              <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                <div className="h-4 w-4 rounded-full bg-gray-200 animate-pulsing" />
              </div>
              <div className="flex-auto py-0.5">
                <div className="h-3 w-48 rounded-md bg-gray-200 animate-pulsing" />
              </div>
              <div className="flex-none py-0.5">
                <div className="h-3 w-20 rounded-md bg-gray-200 animate-pulsing" />
              </div>
            </li>
          ))}
        </ul>
      ) : !history || !history.length ? (
        <div className="text-gray-500 mt-3">No history records found.</div>
      ) : (
        <ul role="list" className="mt-3 space-y-6">
          {history?.map((record, index) => (
            <HistoryRecord
              index={index}
              record={record}
              handleClickRefund={handleClickRefund}
              handleClickRevert={() => handleRevert(record)}
              order={order?.order}
              camp={camp}
              currentUserRole={currentUserRole}
              revertedPaymentIds={revertedPaymentIds}
              historyLength={history.length}
              isLoading={isLoadingRecord}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

const HistoryRecord = ({
  index,
  record,
  handleClickRefund,
  handleClickRevert,
  camp,
  order,
  currentUserRole,
  revertedPaymentIds,
  historyLength,
  isLoading,
}: {
  index: number
  record: IHistoryRecord
  handleClickRefund: any
  handleClickRevert: () => void
  camp: CampProps
  order: IOrder | undefined
  currentUserRole?: eAdminUserRole
  revertedPaymentIds: Set<string>
  historyLength: number
  isLoading: boolean
}) => {
  const { data: currentUser } = useMe()
  const [confirmingRevert, setConfirmingRevert] = useState(false)
  const [showMessage, setShowMessage] = useState(false) // Add this state
  const payment = record.kind === "paid" ? (record.resource as IPayment) : undefined
  const isOffline = payment && payment?.mode === ePaymentMode.MODE_OFFLINE
  const refundedAmount = payment?.refunds?.reduce((acc, refund) => acc + refund.amount_in_cents, 0)
  const isFullyRefunded = Number(refundedAmount) >= Number(payment?.amount_in_cents)
  const isFullyReverted = Number(order?.paid_total_in_cents) === 0

  const handleRevertClick = () => {
    if (confirmingRevert) {
      handleClickRevert()
      setConfirmingRevert(false)
    } else {
      setConfirmingRevert(true)

      setTimeout(() => {
        setConfirmingRevert(false)
      }, 3000)
    }
  }

  return (
    <li key={index} className="relative flex gap-x-4">
      <div
        className={classNames(
          index === historyLength || 0 - 1 ? "h-6" : "-bottom-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-gray-200" />
      </div>
      <>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
          {(() => {
            if (record.kind === "refund") {
              return <ArrowUturnLeftIcon className="h-4 w-4 text-yellow-600" aria-hidden="true" />
            } else if (record.label === "Reservation removed") {
              return <XCircleIcon className="h-4 w-4 text-red-600" aria-hidden="true" />
            } else if (
              (record.kind === "paid" && payment && payment.is_failed && payment.amount_in_cents > 0) ||
              record.label.includes("Failed")
            ) {
              return <XCircleIcon className="h-4 w-4 text-red-600" aria-hidden="true" />
            } else if (record.kind === "paid" && payment && payment.amount_in_cents > 0) {
              return <CheckCircleIcon className="h-4 w-4 text-green-600" aria-hidden="true" />
            } else if (record.kind == "message-sms" || record.kind == "message-mailer") {
              return <EnvelopeIcon className="h-4 w-4 text-gray-600" aria-hidden="true" />
            } else {
              return <div className="h-2 w-2 rounded-full bg-gray-100 ring-1 ring-gray-300" />
            }
          })()}
        </div>
        <p className="flex flex-col py-0.5 text-md leading-5 text-gray-500">
          <div className="flex justify-between">
            {record.external_url ? (
              <HoverTip xs placement="top" content={record.external_url.label}>
                <a
                  className="flex items-center text-blue-500 hover:underline cursor-pointer"
                  onClick={() => {
                    window.open(record?.external_url?.url, "_blank")
                  }}
                >
                  <span className="font-semibold">{record.label}</span>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
                </a>
              </HoverTip>
            ) : (
              <span
                className={classNames(
                  "font-semibold text-gray-900",
                  payment && revertedPaymentIds.has(payment.id) && "line-through",
                )}
              >
                {record.sublabel || record.resource?.reason ? (
                  <div className="flex flex-wrap flex-col">
                    <span className="mr-1"> {record.label}</span>
                    <div className="flex flex-row items-center">
                      {record.kind == "message-sms" ? (
                        <span className="text-gray-450 font-normal mr-2.5">
                          Sent to {formatPhoneNumber(record?.sublabel)}
                        </span>
                      ) : record.kind == "message-mailer" ? (
                        <span className="text-gray-450 font-normal mr-2.5">Sent to {record?.sublabel}</span>
                      ) : (
                        <span className="text-gray-450 font-normal mr-2.5">{record?.sublabel}</span>
                      )}
                      {record.resource?.reason && (
                        <span className="text-gray-450 font-normal">For {record.resource?.reason}</span>
                      )}
                      {record.body && !(record.kind == "message-sms" && record.label != "Custom message") && (
                        <button
                          className="text-blue-500 text-sm font-normal hover:underline"
                          type="button"
                          onClick={() => setShowMessage(!showMessage)}
                        >
                          <EyeIcon className="h-3.5 w-3.5 inline-block mr-1 mb-0.5" />
                          {showMessage ? "Hide message" : "View message"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  record.label
                )}
              </span>
            )}
          </div>
          {showMessage && (
            <div className="mt-1 text-sm text-gray-500 font-normal border-l-2 border-gray-350 ml-1.5 pl-3 pt-1 pb-1 mb-1">
              {record.body}
            </div>
          )}

          <time dateTime={record.datetime} className="py-0.5 text-[12.5px] leading-5 text-gray-400">
            <span className="italic">
              {DateTime.fromISO(record.datetime, { zone: camp.timezone }).toFormat("LLL d, yyyy h:mm a")}
            </span>
            {record.who && (
              <>
                <span className="ml-1.5">by </span>
                <span className="text-sm text-gray-600">
                  {(record.who?.id && record.who.id === currentUser?.id) ||
                  (record.who?.email && record.who.email === currentUser?.email)
                    ? "you"
                    : (record.who?.full_name && record.who?.full_name.trim()) ||
                      (record.who?.email && record.who?.email.trim())}
                </span>
              </>
            )}
          </time>
        </p>
        {currentUserRole && ![eAdminUserRole.UserNoRefund, eAdminUserRole.ViewOnly].includes(currentUserRole) && (
          <div className="flex flex-grow justify-end gap-2">
            {payment && isOffline && !revertedPaymentIds.has(payment.id) && !isFullyReverted && (
              <div className="relative">
                <Button
                  type="button"
                  variant="white"
                  size="small"
                  shadow="small"
                  disabled={!order || isLoading}
                  className={classNames(
                    "group flex-shrink-0 justify-self-end flex h-9",
                    !order && "cursor-not-allowed opacity-30",
                    isLoading && "opacity-70",
                    confirmingRevert ? "text-red-600 bg-red-50" : "text-gray-600",
                  )}
                  onClick={handleRevertClick}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <ArchiveBoxXMarkIcon className="w-5 h-5 transition-transform duration-200 ease-in-out group-hover:scale-110 mr-2" />
                      <div className="truncate">{confirmingRevert && !isMobile ? "Confirm remove" : "Remove"}</div>
                    </>
                  )}
                </Button>
                {confirmingRevert && !isLoading && (
                  <div className="absolute -top-6 left-0 right-0 text-xs text-center text-red-600 whitespace-nowrap">
                    Click again to confirm
                  </div>
                )}
              </div>
            )}
            {record.kind === "paid" &&
              payment &&
              !isFullyRefunded &&
              !isFullyReverted &&
              !revertedPaymentIds.has(payment.id) && (
                <Button
                  type="button"
                  variant="white"
                  size="small"
                  shadow="small"
                  disabled={!order}
                  className={classNames(
                    "group flex-shrink-0 justify-self-end flex text-gray-600 h-9",
                    !order && "cursor-not-allowed opacity-30",
                  )}
                  onClick={() => handleClickRefund(record.resource as IPayment, record?.external_url?.url)}
                  children={
                    <>
                      <ReceiptRefundIcon className="w-5 h-5 mr-2 transition-transform duration-200 ease-in-out group-hover:scale-110" />
                      Refund
                    </>
                  }
                />
              )}
          </div>
        )}
      </>
    </li>
  )
}

export default ReservationHistory
